import { Card, Table, Badge } from 'react-bootstrap'
import { Link } from 'react-router-dom'
import moment from 'moment'
import { AssociatedOrganization } from 'hooks/use-organization-associated-organizations'
import { useState, useEffect } from 'react'
import informationIcon from 'images/information-circle-outline.svg'

interface OrganizationAssociatedOrganizationsTableProps {
  orgId: string
  associatedOrganizations?: AssociatedOrganization[]
  isLoading?: boolean
}

const OrganizationAssociatedOrganizationsTable = ({
  orgId,
  associatedOrganizations,
  isLoading,
}: OrganizationAssociatedOrganizationsTableProps) => {
  // Sort organizations with active subscriptions first
  const sortedOrganizations = associatedOrganizations?.sort((a, b) => {
    const aHasActive = a.subscriptions.some((sub) => sub.status === 'active')
    const bHasActive = b.subscriptions.some((sub) => sub.status === 'active')
    if (aHasActive && !bHasActive) return -1
    if (!aHasActive && bHasActive) return 1
    return 0
  })

  // Create a component for each row to handle data properly
  const AssociatedOrganizationRow = ({
    organization,
    index,
  }: {
    organization: AssociatedOrganization
    index: number
  }) => {
    const createdAt = organization.created_at
      ? moment(organization.created_at).format('MMM DD, YYYY')
      : 'N/A'

    // Get the primary subscription (first active one, or first one if no active)
    const primarySubscription =
      organization.subscriptions.find((sub) => sub.status === 'active') ||
      organization.subscriptions[0]

    const statusVariant =
      primarySubscription?.status === 'active' ? 'success' : 'secondary'

    return (
      <tr key={index}>
        <td style={{ minWidth: '50px' }}>{index + 1}</td>
        <td style={{ minWidth: '200px' }}>
          <Link
            to={`/organizations/${organization.id}`}
            style={{
              color: '#4da6ff',
              textDecoration: 'underline',
              cursor: 'pointer',
              wordBreak: 'break-word',
            }}
          >
            {organization.name}
          </Link>
        </td>
        <td style={{ minWidth: '120px' }}>
          <Badge
            className="text-uppercase"
            style={{
              backgroundColor:
                statusVariant === 'success' ? '#28a745' : '#dc3545',
              color: '#fff',
            }}
          >
            {primarySubscription?.status || 'N/A'}
          </Badge>
        </td>
        <td style={{ minWidth: '100px' }}>
          {primarySubscription?.seat_count || 'N/A'}
        </td>
        <td style={{ minWidth: '180px', maxWidth: '250px' }}>
          {primarySubscription?.subscription_id ? (
            <a
              href={`https://dashboard.stripe.com/subscriptions/${primarySubscription.subscription_id}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: '#4da6ff',
                textDecoration: 'underline',
                wordBreak: 'break-all',
                fontSize: '0.875rem',
              }}
            >
              {primarySubscription.subscription_id}
            </a>
          ) : (
            'N/A'
          )}
        </td>
        <td style={{ minWidth: '120px', whiteSpace: 'nowrap' }}>{createdAt}</td>
      </tr>
    )
  }

  const [showTooltip, setShowTooltip] = useState(false)

  useEffect(() => {
    if (showTooltip) {
      const timer = setTimeout(() => {
        setShowTooltip(false)
      }, 4000)

      return () => clearTimeout(timer)
    }
  }, [showTooltip])

  useEffect(() => {
    const handleClickOutside = () => {
      setShowTooltip(false)
    }

    if (showTooltip) {
      document.addEventListener('click', handleClickOutside)
      return () => document.removeEventListener('click', handleClickOutside)
    }
  }, [showTooltip])

  return (
    <Card className="mt-4">
      <Card.Header className="position-relative">
        <div className="d-flex justify-content-between align-items-center">
          <h4 className="mb-0">
            Associated Organizations
            <img
              src={informationIcon}
              className="ms-2"
              style={{
                cursor: 'pointer',
                width: '25px',
                height: '25px',
                transition: 'transform 0.2s ease',
              }}
              onClick={(e) => {
                e.stopPropagation()
                setShowTooltip(!showTooltip)
              }}
              onMouseEnter={(e) => {
                ;(e.target as HTMLElement).style.transform = 'scale(1.1)'
              }}
              onMouseLeave={(e) => {
                ;(e.target as HTMLElement).style.transform = 'scale(1)'
              }}
              alt="Information"
            />
          </h4>
        </div>

        {showTooltip && (
          <div
            className="position-absolute text-white p-3 "
            style={{
              top: '100%',
              left: '10px',
              fontSize: '0.875rem',
              maxWidth: '300px',
              zIndex: 1000,
              backgroundColor: '#000',
              borderRadius: '10px',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <p>
              Associated Organizations are linked when they share the same
              Stripe customer ID on their Stripe subscription.
            </p>
            <p>
              If you see an organization that is not associated with this
              organization, please contact Engineering team.
            </p>
            <div
              className="position-absolute"
              style={{
                bottom: '100%',
                left: '120px',
                border: '8px solid transparent',
                borderBottomColor: '#000',
              }}
            />
          </div>
        )}
      </Card.Header>
      <Card.Body>
        {isLoading ? (
          <div className="m-2">Loading...</div>
        ) : (
          <>
            {sortedOrganizations && sortedOrganizations.length > 0 ? (
              <div
                className="table-responsive"
                style={{
                  maxHeight: '500px',
                  overflowY: 'auto',
                  border: '1px solid #495057',
                  borderRadius: '0.375rem',
                }}
              >
                <Table
                  striped
                  bordered
                  hover
                  variant="dark"
                  className="mb-0"
                  responsive
                  style={{ borderRadius: '0', minWidth: '800px' }}
                >
                  <thead
                    style={{
                      position: 'sticky',
                      top: 0,
                      zIndex: 100,
                      backgroundColor: '#343a40',
                    }}
                  >
                    <tr>
                      <th style={{ borderTop: 'none', minWidth: '50px' }}>#</th>
                      <th style={{ borderTop: 'none', minWidth: '200px' }}>
                        Organization Name
                      </th>
                      <th style={{ borderTop: 'none', minWidth: '120px' }}>
                        {' '}
                        Subscription Status
                      </th>
                      <th style={{ borderTop: 'none', minWidth: '100px' }}>
                        Seat Count
                      </th>
                      <th style={{ borderTop: 'none', minWidth: '180px' }}>
                        Stripe Subscription ID
                      </th>
                      <th style={{ borderTop: 'none', minWidth: '120px' }}>
                        Created At
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedOrganizations.map((organization, index) => (
                      <AssociatedOrganizationRow
                        key={index}
                        organization={organization}
                        index={index}
                      />
                    ))}
                  </tbody>
                </Table>
              </div>
            ) : (
              <div className="text-center py-4">
                <p>No associated organizations found.</p>
              </div>
            )}
          </>
        )}
      </Card.Body>
    </Card>
  )
}

export default OrganizationAssociatedOrganizationsTable
